// src/pages/CheckoutPage.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { useUser } from '../context/UserContext';
import { addressService } from '../services/addressService';
import { orderService } from '../services/orderService';
import AddressModal from '../components/Address/AddressModal'; 

// Helper to load Razorpay Script dynamically
const loadRazorpayScript = () => {
  return new Promise((resolve) => {
    if (window.Razorpay) {
      resolve(true);
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
};

const CheckoutPage = () => {
  const navigate = useNavigate();
  const { cartItems, getCartTotal, clearCart, cartData, deliveryConfig } = useCart();
  const { user, isLoggedIn } = useUser();

  const [paymentMethod, setPaymentMethod] = useState('PREPAID'); 
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [orderPlaced, setOrderPlaced] = useState(false);
  const [placedOrderDetails, setPlacedOrderDetails] = useState(null);

  // Address States
  const [addresses, setAddresses] = useState([]);
  const [selectedAddressId, setSelectedAddressId] = useState(null);
  const [isAddressModalOpen, setIsAddressModalOpen] = useState(false);
  const [loadingAddresses, setLoadingAddresses] = useState(true);

  // Dynamic Calculations respecting Backend values
  const cartTotal = cartData?.totalAmt || getCartTotal();
  const totalTax = cartData?.totalTax || 0; 
  
  const isFreeDelivery = cartTotal >= (deliveryConfig?.freeDeliveryAbove || 0);
  const appliedDeliveryFee = cartData?.deliveryFee !== undefined ? cartData.deliveryFee : (isFreeDelivery ? 0 : (deliveryConfig?.deliveryFee || 0));
  
  const discountAmt = cartData?.discountAmt || 0;
  const appliedCodCharge = paymentMethod === 'COD' ? (deliveryConfig?.codCharge || 0) : 0;
  
  const baseTotal = cartData?.payableAmt !== undefined ? cartData.payableAmt : (cartTotal + totalTax + appliedDeliveryFee - discountAmt);
  const finalTotal = baseTotal + appliedCodCharge;

  useEffect(() => {
    if (!isLoggedIn()) {
      navigate('/login');
      return;
    }
    if (cartItems.length === 0 && !orderPlaced) {
      navigate('/cart');
      return;
    }
    fetchSavedAddresses();
  }, [isLoggedIn, cartItems.length, orderPlaced, navigate]);

  const fetchSavedAddresses = async () => {
    setLoadingAddresses(true);
    try {
      const data = await addressService.getAll();
      setAddresses(data || []);
      if (data && data.length > 0 && !selectedAddressId) {
        const primary = data.find(a => a.isPrimary) || data[0];
        setSelectedAddressId(primary.id);
      }
    } catch (err) {
      console.error("Error fetching addresses", err);
    } finally {
      setLoadingAddresses(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedAddressId) {
      alert("Please select a delivery address.");
      return;
    }

    if (!cartData?.cartId) {
      alert("Cart details missing. Please refresh and try again.");
      return;
    }

    setIsSubmitting(true);

    try {
      if (paymentMethod === 'COD') {
        const payload = {
          cartId: cartData.cartId,
          addressId: selectedAddressId,
          paymentType: 'COD'
        };
        const res = await orderService.placeOrder(payload);
        
        setPlacedOrderDetails(res);
        setOrderPlaced(true);
        clearCart(true); 
        setIsSubmitting(false);

      } else if (paymentMethod === 'PREPAID') {
        const scriptLoaded = await loadRazorpayScript();
        if (!scriptLoaded) {
          alert("Razorpay SDK failed to load. Please check your internet connection.");
          setIsSubmitting(false);
          return;
        }

        const payload = {
          cartId: cartData.cartId,
          addressId: selectedAddressId,
          paymentType: 'PREPAID'
        };

        const orderRes = await orderService.placeOrder(payload);
        const selAddr = addresses.find(a => a.id === selectedAddressId);

        const options = {
          key: orderRes?.razorpayKey || orderRes?.key || orderRes?.keyId || '',
          amount: orderRes?.amount || (Math.round(finalTotal) * 100),
          currency: orderRes?.currency || 'INR',
          name: 'SV Mart',
          description: 'E-Commerce Order Payment',
          order_id: orderRes?.razorpayOrderId || orderRes?.orderId || orderRes?.id,
          handler: async function (response) {
            try {
              setIsSubmitting(true);
              const verifyPayload = {
                cartId: cartData.cartId,
                addressId: selectedAddressId,
                paymentType: 'PREPAID',
                razorpayOrderId: response.razorpay_order_id,
                razorpayPaymentId: response.razorpay_payment_id,
                razorpaySignature: response.razorpay_signature
              };
              const verifyRes = await orderService.verifyPayment(verifyPayload);
              
              setPlacedOrderDetails(verifyRes);
              setOrderPlaced(true);
              clearCart(true); 
            } catch (verifyErr) {
              console.error("Payment verification error:", verifyErr);
              alert(verifyErr.response?.data?.message || "Payment verification failed. Please contact support.");
            } finally {
              setIsSubmitting(false);
            }
          },
          prefill: {
            name: selAddr?.receiverName || user?.name || '',
            email: user?.email || '',
            contact: selAddr?.phoneNumber || ''
          },
          theme: {
            color: '#0F2F63'
          },
          modal: {
            ondismiss: function() {
              setIsSubmitting(false);
            }
          }
        };

        const rzp = new window.Razorpay(options);
        rzp.on('payment.failed', function (response){
            setIsSubmitting(false);
            alert("Payment Failed. Reason: " + response.error.description);
        });
        rzp.open();
      }
    } catch (err) {
      console.error("Error initiating order:", err);
      alert(err.response?.data?.message || err.response?.data || "Failed to place order. Please try again.");
      setIsSubmitting(false);
    }
  };

  if (orderPlaced) {
    const displayOrderId = placedOrderDetails?.globalOrderId || placedOrderDetails?.orders?.[0]?.orderId || Date.now().toString().slice(-8);
    return (
      <div className="max-w-[1200px] mx-auto px-4 py-12">
        <div className="text-center py-20 bg-white rounded-3xl border border-gray-100 shadow-sm max-w-2xl mx-auto">
          <i className="fas fa-check-circle text-7xl text-[#2CCE75] mb-6"></i>
          <h2 className="text-3xl font-black text-[#0F2F63] mb-3">Order Placed Successfully!</h2>
          <p className="text-gray-500 font-medium mb-2">Thank you for your order. We'll deliver it to your doorstep soon.</p>
          <p className="text-[#FF7A59] font-bold text-lg mb-8">Order ID: #{displayOrderId}</p>
          <Link to="/quick" className="inline-block px-8 py-3.5 bg-[#0F2F63] hover:bg-[#1a4a8a] text-white rounded-xl font-bold transition-all shadow-md">
            Continue Shopping
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-[1200px] mx-auto px-4 py-6 md:py-8">
      
      {/* Header */}
      <div className="flex items-center gap-4 mb-8">
        <button 
          onClick={() => navigate('/cart')}
          className="w-10 h-10 rounded-full bg-white shadow-sm border border-gray-200 hover:bg-gray-50 flex items-center justify-center text-gray-600 transition-colors"
        >
          <i className="fas fa-arrow-left"></i>
        </button>
        <h1 className="text-2xl md:text-3xl font-black text-[#0F2F63] flex items-center gap-3">
          <i className="fas fa-clipboard-list text-[#FF7A59]"></i> Checkout
        </h1>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[2fr_1fr] gap-6 lg:gap-8 items-start">
        
        {/* Form Section */}
        <div className="bg-white p-6 md:p-8 rounded-3xl border border-gray-100 shadow-sm">
          
          <div className="flex items-center justify-between border-b border-gray-100 pb-4 mb-6">
            <h3 className="text-lg font-black text-gray-900 uppercase tracking-widest">
              Delivery Address
            </h3>
            <button onClick={() => setIsAddressModalOpen(true)} className="text-xs font-bold text-sky-600 hover:text-sky-800 bg-sky-50 hover:bg-sky-100 px-3 py-1.5 rounded-lg transition-colors flex items-center gap-1.5">
              <i className="fas fa-plus"></i> Add New
            </button>
          </div>

          {loadingAddresses ? (
            <div className="flex flex-col items-center justify-center py-10">
              <i className="fas fa-spinner fa-spin text-2xl text-[#0F2F63] mb-2"></i>
              <p className="text-gray-500 font-medium text-sm">Loading addresses...</p>
            </div>
          ) : addresses.length === 0 ? (
            <div className="text-center py-10 bg-gray-50 border border-dashed border-gray-200 rounded-2xl mb-6">
              <i className="fas fa-map-marker-alt text-4xl text-gray-300 mb-3"></i>
              <p className="text-gray-600 font-bold mb-4">No delivery address found.</p>
              <button onClick={() => setIsAddressModalOpen(true)} className="bg-[#0F2F63] text-white px-6 py-2.5 rounded-xl text-sm font-bold shadow-md hover:bg-[#1a4a8a]">
                Add Delivery Address
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
              {addresses.map(addr => (
                <div 
                  key={addr.id} 
                  onClick={() => setSelectedAddressId(addr.id)}
                  className={`p-4 rounded-2xl border-2 cursor-pointer transition-all relative ${selectedAddressId === addr.id ? 'border-[#2CCE75] bg-emerald-50/20' : 'border-gray-100 hover:border-gray-200 bg-gray-50/50'}`}
                >
                  {selectedAddressId === addr.id && <div className="absolute top-3 right-3 w-5 h-5 bg-[#2CCE75] rounded-full flex items-center justify-center text-white text-[10px] shadow-sm"><i className="fas fa-check"></i></div>}
                  <div className="flex items-center gap-2 mb-2">
                    <span className="bg-white border border-gray-200 text-gray-600 px-2 py-0.5 rounded text-[10px] font-black uppercase tracking-widest flex items-center gap-1">
                      <i className={`fas ${addr.addressType === 'HOME' ? 'fa-home' : addr.addressType === 'WORK' ? 'fa-briefcase' : 'fa-map-marker-alt'}`}></i> {addr.addressType}
                    </span>
                  </div>
                  <h4 className="text-sm font-bold text-gray-900 mb-1">{addr.receiverName}</h4>
                  <p className="text-xs text-gray-500 font-medium leading-relaxed mb-2 line-clamp-2">
                    {addr.houseNumber}, {addr.streetName}, {addr.city}
                  </p>
                  <p className="text-xs font-bold text-gray-700 flex items-center gap-1.5">
                    <i className="fas fa-phone-alt text-gray-400"></i> {addr.phoneNumber}
                  </p>
                </div>
              ))}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            {/* Payment Options */}
            <h3 className="text-lg font-black text-gray-900 mb-4 pb-4 border-b border-gray-100 uppercase tracking-widest mt-8">
              Payment Method
            </h3>
            
            <div className="flex flex-col gap-3">
              <div 
                onClick={() => setPaymentMethod('PREPAID')}
                className={`flex items-center gap-4 p-4 border-2 rounded-xl cursor-pointer transition-all ${
                  paymentMethod === 'PREPAID' ? 'border-[#2CCE75] bg-emerald-50/50' : 'border-gray-100 hover:border-gray-200 bg-white'
                }`}
              >
                <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${paymentMethod === 'PREPAID' ? 'border-[#2CCE75]' : 'border-gray-300'}`}>
                  {paymentMethod === 'PREPAID' && <div className="w-2.5 h-2.5 bg-[#2CCE75] rounded-full"></div>}
                </div>
                <div className="flex-1">
                  <p className="text-sm font-bold text-gray-900">Online Payment</p>
                  <p className="text-xs text-gray-500 font-medium mt-0.5">Pay securely via Cards, UPI, Netbanking & Wallets</p>
                </div>
                <div className="flex items-center gap-2 text-xl text-gray-400">
                  <i className="fas fa-credit-card"></i>
                  <i className="fas fa-mobile-alt"></i>
                </div>
              </div>

              <div 
                onClick={() => setPaymentMethod('COD')}
                className={`flex items-center gap-4 p-4 border-2 rounded-xl cursor-pointer transition-all ${
                  paymentMethod === 'COD' ? 'border-[#2CCE75] bg-emerald-50/50' : 'border-gray-100 hover:border-gray-200 bg-white'
                }`}
              >
                <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${paymentMethod === 'COD' ? 'border-[#2CCE75]' : 'border-gray-300'}`}>
                  {paymentMethod === 'COD' && <div className="w-2.5 h-2.5 bg-[#2CCE75] rounded-full"></div>}
                </div>
                <div className="flex-1">
                  <p className="text-sm font-bold text-gray-900">Cash on Delivery</p>
                  {/* FIXED: Removed Decimals */}
                  {deliveryConfig?.codCharge > 0 && <p className="text-xs text-gray-500 font-semibold mt-0.5">Additional charge: ₹ {Math.round(deliveryConfig.codCharge)}</p>}
                </div>
                <i className="fas fa-money-bill-wave text-xl text-gray-400"></i>
              </div>
            </div>

            <button 
              type="submit" 
              disabled={isSubmitting || !selectedAddressId}
              className="lg:hidden w-full mt-6 py-4 bg-[#2CCE75] hover:bg-[#1EA85E] text-white rounded-xl text-base font-black transition-all shadow-lg shadow-emerald-200 flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed"
            >
              {isSubmitting ? (
                <><i className="fas fa-spinner fa-spin"></i> Processing...</>
              ) : (
                <><i className="fas fa-lock"></i> Place Order ₹ {Math.round(finalTotal)}</>
              )}
            </button>

          </form>
        </div>

        {/* Order Summary Sidebar */}
        <div className="bg-white p-6 md:p-8 rounded-3xl border border-gray-100 shadow-sm sticky top-[150px]">
          <h3 className="text-lg font-black text-gray-900 mb-6 pb-4 border-b border-gray-100 uppercase tracking-widest">
            Order Summary
          </h3>
          
          <div className="max-h-[250px] overflow-y-auto mb-6 pr-2 space-y-4 custom-scrollbar">
            {cartItems.map((item) => {
              const vId = String(item.cartItemId || item.outletVariantId || item.variantId || item.id || '');
              return (
              <div className="flex justify-between items-start gap-4 pb-4 border-b border-gray-50 last:border-0 last:pb-0" key={vId}>
                <div className="flex-1">
                  <p className="text-sm font-bold text-gray-800 line-clamp-2 leading-tight mb-0.5">
                    {item.productName || item.name}
                  </p>
                  {/* FIXED: Added Variant Name Display */}
                  <p className="text-xs text-gray-500 font-medium mb-1">
                    {item.variantName || item.pack || '1 Pack'}
                  </p>
                  {/* FIXED: Removed Decimals */}
                  <p className="text-xs font-semibold text-gray-400">
                    Qty: {item.quantity || 1} {item.taxRate > 0 && `| Tax: ₹ ${Math.round(item.taxRate)}`}
                  </p>
                </div>
                {/* FIXED: Removed Decimals */}
                <span className="text-sm font-black text-[#0F2F63]">
                  ₹ {Math.round((item.sellingPrice || item.price) * (item.quantity || 1))}
                </span>
              </div>
            )})}
          </div>

          <div className="space-y-4 mb-6 border-t border-gray-100 pt-6">
            <div className="flex justify-between text-sm font-bold text-gray-500">
              <span>Subtotal</span>
              {/* FIXED: Removed Decimals */}
              <span className="text-gray-900">₹ {Math.round(cartTotal)}</span>
            </div>
            
            {discountAmt > 0 && (
              <div className="flex justify-between text-sm font-bold text-emerald-500">
                <span>Coupon Discount</span>
                {/* FIXED: Removed Decimals */}
                <span>- ₹ {Math.round(discountAmt)}</span>
              </div>
            )}
            
            {totalTax > 0 && (
              <div className="flex justify-between text-sm font-bold text-gray-500">
                <span>Tax</span>
                <span className="text-gray-900">₹ {Math.round(totalTax)}</span>
              </div>
            )}

            <div className="flex justify-between text-sm font-bold text-gray-500">
              <span>Delivery Fee</span>
              <span className={appliedDeliveryFee === 0 ? "text-[#2CCE75]" : "text-gray-900"}>
                {appliedDeliveryFee === 0 ? 'FREE' : `₹ ${Math.round(appliedDeliveryFee)}`}
              </span>
            </div>

            {paymentMethod === 'COD' && appliedCodCharge > 0 && (
              <div className="flex justify-between text-sm font-bold text-orange-500">
                <span>COD Charge</span>
                <span>+ ₹ {Math.round(appliedCodCharge)}</span>
              </div>
            )}
          </div>
          
          <div className="flex justify-between items-center py-5 border-t-2 border-gray-100 mb-6">
            <span className="text-base font-black text-gray-900 uppercase">Total</span>
            <span className="text-2xl font-black text-[#0F2F63]">₹ {Math.round(finalTotal)}</span>
          </div>

          <div className="flex items-center gap-2.5 p-3 bg-emerald-50 rounded-xl text-xs font-bold text-[#2CCE75] mb-6 border border-emerald-100">
            <i className="fas fa-truck text-base"></i>
            <span>
              {isFreeDelivery 
                ? 'Free delivery applied on this order!' 
                : `Free delivery above ₹ ${Math.round(deliveryConfig?.freeDeliveryAbove || 0)}`}
            </span>
          </div>

          <button 
            onClick={handleSubmit}
            disabled={isSubmitting || !selectedAddressId}
            className="hidden lg:flex w-full py-4 bg-[#2CCE75] hover:bg-[#1EA85E] text-[#ffffff] rounded-xl text-base font-black transition-all shadow-lg shadow-emerald-200 items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed"
          >
            {isSubmitting ? (
              <><i className="fas fa-spinner fa-spin"></i> Processing...</>
            ) : (
              <><i className="fas fa-lock"></i> Place Order</>
            )}
          </button>

        </div>
      </div>

      <AddressModal 
        isOpen={isAddressModalOpen} 
        onClose={() => setIsAddressModalOpen(false)} 
        onSuccess={fetchSavedAddresses} 
      />

    </div>
  );
};

export default CheckoutPage;